import {
  AnnotationId,
  type CurriculumPhase,
  type CurriculumRole,
  type CurriculumTags,
  DocumentId,
  type PhaseTimestamps,
  type ResolveDecision,
} from "@curriculum-studio/core";
import { type DateTime, Effect, Schema } from "effect";
import { createContext, useContext } from "react";
import { annotateStructured, type StructuredAnchorInput } from "./annotate-structured-action.ts";
import {
  advanceCurriculumPhase,
  cloneAtVersion,
  cloneCurriculum,
  closeCurriculumRework,
  deleteCurriculum,
  importDocument,
  openCurriculumRework,
  publishCurriculum,
  setCurriculumRole,
  setCurriculumTags,
} from "./create-actions.ts";
import type { CurriculumListItem } from "./gate-actions.ts";
import { getCurriculumTags, listCurricula, resolve, uploadDocument } from "./gate-actions.ts";
import { runtime } from "./runtime.ts";
import { strip } from "./strip-action.ts";

// The collab surface's counterpart (ADR-0045): the anchor was either landed or the caller-supplied
// `{ from, to, atVersion }` had already gone stale (the document moved since that version, or the
// version doesn't exist) — the composer surfaces the message and asks the Expert to reselect,
// mirroring `AnnotateAmbiguous`'s shape but naming what can actually go wrong here.
export const AnnotateStructuredOk = Schema.TaggedStruct("ok", {
  annotationId: Schema.String,
  anchor: Schema.Struct({
    from: Schema.Int,
    to: Schema.Int,
    atVersion: Schema.Int,
  }),
});
export const AnnotateStructuredStale = Schema.TaggedStruct("stale", { message: Schema.String });
export const AnnotateStructuredResult = Schema.Union(AnnotateStructuredOk, AnnotateStructuredStale);
export type AnnotateStructuredResult = typeof AnnotateStructuredResult.Type;

// Resolving awaits a decided outcome: the decision landed (with the ids it cleared), or it lost the
// first-writer-wins race (`409`, the finding was already decided). The conflict is a value the
// gate UI acts on — surface the message and resync — not a swallowed rejection (R7).
export const ResolveOk = Schema.TaggedStruct("ok", {
  clearedAnnotationIds: Schema.Array(AnnotationId),
});
export const ResolveConflict = Schema.TaggedStruct("conflict", { message: Schema.String });
export const ResolveResult = Schema.Union(ResolveOk, ResolveConflict);
export type ResolveResult = typeof ResolveResult.Type;

// Stripping awaits a decided outcome: the retraction landed, or the annotation was decided out
// from under the Expert (a concurrent Gate-1 keep/reject/close won the race). Other failures
// reject as usual.
export const StripOk = Schema.TaggedStruct("ok", { annotationId: Schema.String });
export const StripAlreadyDecided = Schema.TaggedStruct("already-decided", {
  message: Schema.String,
});
export const StripResult = Schema.Union(StripOk, StripAlreadyDecided);
export type StripResult = typeof StripResult.Type;

// Creating a document (clone or import) awaits a decided outcome: the new document was minted (with
// its id, so the UI can navigate to it), or the request failed (404 source-deleted, 422
// empty/markup/oversized). The failure is a value the index UI shows inline — surface the status and
// message — not a swallowed rejection, mirroring `ResolveConflict`.
export const CreateOk = Schema.TaggedStruct("ok", { documentId: DocumentId });
export const CreateFailed = Schema.TaggedStruct("failed", {
  status: Schema.Number,
  message: Schema.String,
});
export const CreateResult = Schema.Union(CreateOk, CreateFailed);
export type CreateResult = typeof CreateResult.Type;

// The outcome of an ad-hoc chat upload (chat-context-attach). `ok` carries the server-minted Files API
// `fileId` + echoed name the composer references on the turn; `failed` carries the server's own
// message (the oversize 413's "… is too large to attach", or a generic transport/server fault) so the
// composer surfaces the specific reason instead of a blanket "Couldn't upload". Mirrors `ProposeEditResult`.
export const UploadOk = Schema.TaggedStruct("ok", {
  fileId: Schema.String,
  name: Schema.String,
});
export const UploadFailed = Schema.TaggedStruct("failed", { message: Schema.String });
export const UploadResult = Schema.Union(UploadOk, UploadFailed);
export type UploadResult = typeof UploadResult.Type;

// The component-facing seam over `gate-actions` (N8): the REST half is `Promise`-returning
// functions so React event handlers can `await` them without touching Effect. The live value runs
// each Effect through the app `runtime`; tests inject a stub `GateActions` to assert the gate-button
// request shape without a live `HttpClient`.
export interface GateActions {
  readonly resolve: (documentId: DocumentId, decision: ResolveDecision) => Promise<ResolveResult>;
  readonly listCurricula: (
    roles?: ReadonlyArray<CurriculumRole>,
  ) => Promise<readonly CurriculumListItem[]>;
  // Ingest an ad-hoc chat upload (chat-context-attach): POST the file's raw bytes + its `File.type`
  // (so the server labels the Files API upload with its real MIME), resolve `ok` with the
  // server-minted Files API `fileId` + echoed name, or `failed` with the server's message (413
  // oversize / transport fault) — never rejects for a known failure, mirroring `proposeEdit`.
  readonly uploadDocument: (
    name: string,
    contentType: string,
    bytes: Uint8Array,
  ) => Promise<UploadResult>;
  // The collab surface's counterpart (ADR-0045): the anchor is the selection's exact
  // `{ from, to, atVersion }` (D12) — no `Locator`, no scope. Used by `selection-annotate.tsx`
  // on the CM host, which already has the offsets and the collabVersion it read them at.
  readonly annotateSelection: (
    documentId: DocumentId,
    anchor: StructuredAnchorInput,
    comment: string,
  ) => Promise<AnnotateStructuredResult>;
  readonly strip: (documentId: DocumentId, annotationId: AnnotationId) => Promise<StripResult>;
  readonly cloneCurriculum: (sourceId: DocumentId) => Promise<CreateResult>;
  // Clone from a chosen historical version (Clone from history): the History rail passes the source
  // doc id and the selected row's version. Same decided-result surface as `cloneCurriculum`.
  readonly cloneAtVersion: (sourceId: DocumentId, atVersion: number) => Promise<CreateResult>;
  // Import requires the identity tags (level + language, plus the learner's `l1`) and takes an
  // optional role and phase, both of which default server-side (canonical / structure).
  readonly importDocument: (
    text: string,
    tags: CurriculumTags,
    role?: CurriculumRole,
    phase?: CurriculumPhase,
  ) => Promise<CreateResult>;
  // Set (or re-set) one curriculum's IDENTITY tags — the Expert's identity-edit affordance on the
  // curriculum page (never the index).
  readonly setCurriculumTags: (
    documentId: DocumentId,
    tags: CurriculumTags,
  ) => Promise<CreateResult>;
  // The four lifecycle writes (the status-axis split), all Expert/UI-only from the curriculum page.
  // `setCurriculumRole` names the incumbent on a promotion; `advanceCurriculumPhase` is refused (409)
  // if it would not move forward; `publishCurriculum` pins the current head; the rework pair sends
  // part of a live document back to an earlier phase and brings it back.
  readonly setCurriculumRole: (
    documentId: DocumentId,
    role: CurriculumRole,
    supersedes?: DocumentId,
  ) => Promise<CreateResult>;
  readonly advanceCurriculumPhase: (
    documentId: DocumentId,
    phase: CurriculumPhase,
  ) => Promise<CreateResult>;
  readonly publishCurriculum: (documentId: DocumentId) => Promise<CreateResult>;
  readonly openCurriculumRework: (
    documentId: DocumentId,
    phase: CurriculumPhase,
  ) => Promise<CreateResult>;
  readonly closeCurriculumRework: (documentId: DocumentId) => Promise<CreateResult>;
  // One document's identity plus where it stands, for the doc-view header. `tags` undefined =
  // untagged; role and phase always resolve.
  readonly getCurriculumTags: (documentId: DocumentId) => Promise<{
    readonly tags?: CurriculumTags;
    readonly role: CurriculumRole;
    readonly phase: CurriculumPhase;
    readonly publishedVersion?: number;
    readonly reworkPhase?: CurriculumPhase;
    readonly phaseEnteredAt?: PhaseTimestamps;
    readonly publishedAt?: DateTime.Utc;
  }>;
  // Permanently delete an ARCHIVED curriculum (Expert/UI-only). Resolves to the same tagged result
  // the clone/import actions use, so the index surfaces failures inline.
  readonly deleteCurriculum: (documentId: DocumentId) => Promise<CreateResult>;
}

// The decided result for a create/lifecycle failure that is NOT the server's tagged 4xx — a network
// transport error, or a response-decode `ParseError`. `status: 0` marks it as a non-HTTP failure so
// the UI can still show something actionable inline. The gate interface promises a resolved
// `CreateResult`, so these must collapse to a value, never a rejection.
const REQUEST_FAILED_FALLBACK = CreateFailed.make({
  status: 0,
  message: "The request failed. Check your connection and try again.",
});

// Collapse a lifecycle action's failures into the same inline-showable result the clone/import
// actions resolve to: the tagged `CreateRequestFailed` carries the server's status + message; any
// other failure (transport / decode) collapses to the generic fallback so the action never rejects.
const lifecycleResult = (action: ReturnType<typeof deleteCurriculum>) =>
  action.pipe(
    Effect.map((success) => CreateOk.make({ documentId: success.documentId })),
    Effect.catchTag("CreateRequestFailed", (error) =>
      Effect.succeed(CreateFailed.make({ status: error.status, message: error.message })),
    ),
    Effect.catchAll(() => Effect.succeed(REQUEST_FAILED_FALLBACK)),
  );

export const liveGateActions: GateActions = {
  resolve: (documentId, decision) =>
    runtime.runPromise(
      resolve(documentId, decision).pipe(
        Effect.map((success) =>
          ResolveOk.make({ clearedAnnotationIds: success.clearedAnnotationIds }),
        ),
        Effect.catchTag("ResolveAlreadyDecided", (error) =>
          Effect.succeed(ResolveConflict.make({ message: error.message })),
        ),
      ),
    ),
  listCurricula: (roles) => runtime.runPromise(listCurricula(roles)),
  uploadDocument: (name, contentType, bytes) =>
    runtime.runPromise(
      uploadDocument(name, contentType, bytes).pipe(
        Effect.map((success) => UploadOk.make({ fileId: success.fileId, name: success.name })),
        Effect.catchTag("UploadRequestFailed", (error) =>
          Effect.succeed(UploadFailed.make({ message: error.message })),
        ),
        Effect.catchAll(() =>
          Effect.succeed(UploadFailed.make({ message: `Couldn't upload "${name}".` })),
        ),
      ),
    ),
  annotateSelection: (documentId, anchor, comment) =>
    runtime.runPromise(
      annotateStructured(documentId, anchor, comment).pipe(
        Effect.map((success) =>
          AnnotateStructuredOk.make({
            annotationId: success.annotationId,
            anchor: success.anchor,
          }),
        ),
        Effect.catchTag("AnnotateStructuredRejected", (error) =>
          Effect.succeed(AnnotateStructuredStale.make({ message: error.message })),
        ),
      ),
    ),
  strip: (documentId, annotationId) =>
    runtime.runPromise(
      strip(documentId, annotationId).pipe(
        Effect.map((success) => StripOk.make({ annotationId: success.annotationId })),
        Effect.catchTag("StripAlreadyDecided", (error) =>
          Effect.succeed(StripAlreadyDecided.make({ message: error.message })),
        ),
      ),
    ),
  cloneCurriculum: (sourceId) =>
    runtime.runPromise(
      cloneCurriculum(sourceId).pipe(
        Effect.map((success) => CreateOk.make({ documentId: success.documentId })),
        Effect.catchTag("CreateRequestFailed", (error) =>
          Effect.succeed(CreateFailed.make({ status: error.status, message: error.message })),
        ),
        Effect.catchAll(() => Effect.succeed(REQUEST_FAILED_FALLBACK)),
      ),
    ),
  cloneAtVersion: (sourceId, atVersion) =>
    runtime.runPromise(
      cloneAtVersion(sourceId, atVersion).pipe(
        Effect.map((success) => CreateOk.make({ documentId: success.documentId })),
        Effect.catchTag("CreateRequestFailed", (error) =>
          Effect.succeed(CreateFailed.make({ status: error.status, message: error.message })),
        ),
        Effect.catchAll(() => Effect.succeed(REQUEST_FAILED_FALLBACK)),
      ),
    ),
  importDocument: (text, tags, status) =>
    runtime.runPromise(
      importDocument(text, tags, status).pipe(
        Effect.map((success) => CreateOk.make({ documentId: success.documentId })),
        Effect.catchTag("CreateRequestFailed", (error) =>
          Effect.succeed(CreateFailed.make({ status: error.status, message: error.message })),
        ),
        Effect.catchAll(() => Effect.succeed(REQUEST_FAILED_FALLBACK)),
      ),
    ),
  setCurriculumTags: (documentId, tags) =>
    runtime.runPromise(lifecycleResult(setCurriculumTags(documentId, tags))),
  setCurriculumRole: (documentId, role, supersedes) =>
    runtime.runPromise(lifecycleResult(setCurriculumRole(documentId, role, supersedes))),
  advanceCurriculumPhase: (documentId, phase) =>
    runtime.runPromise(lifecycleResult(advanceCurriculumPhase(documentId, phase))),
  publishCurriculum: (documentId) =>
    runtime.runPromise(lifecycleResult(publishCurriculum(documentId))),
  openCurriculumRework: (documentId, phase) =>
    runtime.runPromise(lifecycleResult(openCurriculumRework(documentId, phase))),
  closeCurriculumRework: (documentId) =>
    runtime.runPromise(lifecycleResult(closeCurriculumRework(documentId))),
  getCurriculumTags: (documentId) =>
    runtime.runPromise(
      getCurriculumTags(documentId).pipe(
        Effect.map((view) => ({
          ...(view.tags !== undefined ? { tags: view.tags } : {}),
          role: view.role,
          phase: view.phase,
          ...(view.publishedVersion !== undefined
            ? { publishedVersion: view.publishedVersion }
            : {}),
          ...(view.reworkPhase !== undefined ? { reworkPhase: view.reworkPhase } : {}),
          ...(view.phaseEnteredAt !== undefined ? { phaseEnteredAt: view.phaseEnteredAt } : {}),
          ...(view.publishedAt !== undefined ? { publishedAt: view.publishedAt } : {}),
        })),
      ),
    ),
  deleteCurriculum: (documentId) =>
    runtime.runPromise(lifecycleResult(deleteCurriculum(documentId))),
};

const GateActionsContext = createContext<GateActions>(liveGateActions);

export const GateActionsProvider = GateActionsContext.Provider;

export const useGateActions = (): GateActions => useContext(GateActionsContext);
